
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import type { Athlete, Course } from "../types";
import type { OffsetAdjustment, RaceIncident, LapCrossing } from "./raceAnalysis";
import type { DraftingStats } from "./draftingAnalysis";

interface RaceAnalysisResult {
    offsets: Map<string, OffsetAdjustment[]>;
    incidents: Map<string, RaceIncident[]>;
}

export const cloudAnalysisService = {
    /**
     * Calls the protected 'analyzeRaceOffsets' Cloud Function.
     * This logic determines Late Starts, Re-integrations, and Incidents.
     */
    getRaceAnalysis: async (raceData: { athletes: Athlete[], course: Course, startTime: number }, raceId: string): Promise<RaceAnalysisResult> => {
        try {
            // Function name must match the CamelCase export in functions/src/index.ts exactly (case-sensitive)
            const analyzeRaceOffsets = httpsCallable(functions, 'analyzeRaceOffsets');
            
            // PAYLOAD OPTIMIZATION & SANITIZATION:
            // 1. Use Compact Format (Array of Arrays) to reduce JSON size by ~50%.
            // 2. Filter out invalid points (NaN, null, Infinity) to prevent Server Crash.
            
            const optimizedAthletes = raceData.athletes.map(a => {
                const validTrackData: number[][] = [];
                
                if (a.track) {
                    for (const p of a.track) {
                        // Ensure all required fields are valid FINITE numbers
                        // JSON.stringify converts NaN/Infinity to null, which causes server crashes on date parsing
                        if (
                            Number.isFinite(p.lat) && 
                            Number.isFinite(p.lon) && 
                            Number.isFinite(p.timestamp) && 
                            Number.isFinite(p.distance)
                        ) {
                            validTrackData.push([
                                p.lat, 
                                p.lon, 
                                p.timestamp, 
                                p.distance
                            ]);
                        }
                    }
                }

                return {
                    id: a.id,
                    name: a.name,
                    weight: a.weight,
                    trackData: validTrackData // Send compact array
                };
            });

            // IMPORTANT: Do NOT send course.path. It is heavy and unused by the offset logic (which only needs Start/Finish).
            const leanCourse = {
                start: raceData.course.start,
                finish: raceData.course.finish,
                path: [] // Empty path to save bandwidth
            };

            const payload = {
                raceId,
                athletes: optimizedAthletes,
                course: leanCourse, 
                raceStartTime: raceData.startTime || 0 
            };

            // Debug Payload Size
            const jsonPayload = JSON.stringify(payload);
            const sizeBytes = new TextEncoder().encode(jsonPayload).length;
            const sizeMB = (sizeBytes / (1024 * 1024)).toFixed(2);
            console.log(`[Cloud Analysis] Sending Request (analyzeRaceOffsets)... Payload size: ${sizeMB} MB`);

            const result = await analyzeRaceOffsets(payload);

            const responseData = result.data as { 
                offsets: Record<string, OffsetAdjustment[]>, 
                incidents: Record<string, RaceIncident[]>, 
                logs?: string[] 
            };
            
            if (!responseData) {
                console.error("Cloud Analysis (analyzeRaceOffsets) Failed: Response data is null or undefined.");
                return { offsets: new Map(), incidents: new Map() };
            }

            // --- SERVER-SIDE LOGS OUTPUT ---
            if (responseData.logs && responseData.logs.length > 0) {
                console.groupCollapsed("%c[Cloud Analysis Debug Logs]", "color: #22d3ee; font-weight: bold; background: #1f2937; padding: 4px; border-radius: 4px;");
                responseData.logs.forEach(log => console.log(log));
                console.groupEnd();
            }
            // -------------------------------

            const offsetsData = responseData.offsets || {};
            const incidentsData = responseData.incidents || {};
            
            const offsetMap = new Map<string, OffsetAdjustment[]>();
            Object.entries(offsetsData).forEach(([key, value]) => {
                offsetMap.set(key, value);
            });

            const incidentMap = new Map<string, RaceIncident[]>();
            Object.entries(incidentsData).forEach(([key, value]) => {
                incidentMap.set(key, value);
            });
            
            return { offsets: offsetMap, incidents: incidentMap };
        } catch (error: any) {
            // Log detailed error for debugging (e.g. CORS, 404, 413)
            console.error("Cloud Analysis (analyzeRaceOffsets) Failed:", error.code, error.message, error);
            // Return empty maps on failure to prevent UI crash
            return { offsets: new Map(), incidents: new Map() };
        }
    },

    /**
     * Calls the protected 'analyzeRaceStats' Cloud Function.
     * Replaces client-side lap and distance calculations.
     */
    getRaceStats: async (raceData: { athletes: Athlete[], course: Course }): Promise<{ lapCrossings: Record<number, LapCrossing[]>, virtualDistances: Record<string, number> }> => {
        try {
            const analyzeRaceStats = httpsCallable(functions, 'analyzeRaceStats');
            
            // Use compact format for athletes to save bandwidth
            const optimizedAthletes = raceData.athletes.map(a => {
                const validTrackData: number[][] = [];
                if (a.track) {
                    for (const p of a.track) {
                        if (Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
                            validTrackData.push([p.lat, p.lon, p.timestamp, p.distance]);
                        }
                    }
                }
                return { id: a.id, name: a.name, trackData: validTrackData };
            });

            const result = await analyzeRaceStats({ 
                athletes: optimizedAthletes, 
                course: raceData.course 
            });
            
            return result.data as { lapCrossings: Record<number, LapCrossing[]>, virtualDistances: Record<string, number> };
        } catch (error: any) {
            console.error("Cloud Analysis (analyzeRaceStats) Failed:", error);
            return { lapCrossings: {}, virtualDistances: {} };
        }
    },

    /**
     * Calls the protected 'analyzeDrafting' Cloud Function.
     * This logic uses physics to estimate energy saved.
     */
    getDraftingStats: async (athletes: Athlete[], raceId: string): Promise<DraftingStats[]> => {
        try {
            // Function name must match the CamelCase export in functions/src/index.ts
            const analyzeDrafting = httpsCallable(functions, 'analyzeDrafting');
            
            const optimizedAthletes = athletes.map(a => ({
                id: a.id,
                name: a.name,
                weight: a.weight,
                height: a.height,
                bikeWeight: a.bikeWeight,
                track: a.track 
            }));

            const result = await analyzeDrafting({ athletes: optimizedAthletes, raceId });
            return result.data as DraftingStats[];
        } catch (error: any) {
             console.error("Cloud Analysis (analyzeDrafting) Failed:", error.code, error.message);
            return [];
        }
    },

    /**
     * Calls the protected 'generateGeminiInsights' Cloud Function.
     * securely routes the prompt to Gemini via the backend.
     */
    generateAIInsights: async (prompt: string): Promise<string> => {
        try {
            const generateGeminiInsights = httpsCallable(functions, 'generateGeminiInsights');
            const result = await generateGeminiInsights({ prompt });
            const data = result.data as { text: string };
            return data.text;
        } catch (error: any) {
            console.error("AI Generation Failed:", error);
            const msg = error?.message || "Failed to generate insights via Cloud Function.";
            const customError: any = new Error(msg);
            customError.code = error?.code;
            customError.details = error?.details;
            throw customError;
        }
    }
};
